import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import FooterModal from "@components/FooterModal/FooterModal";
import CustomButton from "@components/shared/FormElements/CustomButton/CustomButton";
import PasswordLabel from "@components/shared/FormElements/InputPassword/InputPassword";
import Loader from "@components/shared/Loader/Loader";
import Modals from "@components/shared/Modal/Modal";
import { AVAILIO_LOGO } from "@constants/assets";
import { CONSTANTS } from "@constants/index";
import { routeEnum } from "@enums/route.enum";
import handleResponse from "@services/apiConfig/api.response.service";
import API_SERVICES from "@services/index";
import localSlice from "@slices/local.slice";
import modalSlice from "@slices/modal.slice";
import { RootState } from "@store/index";
import commonUtils from "@utils/common.utils";
const PasswordModalComponent = () => {
  const [passwordStates, setPasswordStates] = useState({
    passwordMismatch: false,
    passwordStrength: false,
  });
  const [tokenValidity, setTokenValidity] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const url = location.href;
  const token = url.split("/").pop() || "";
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const navigate = useNavigate();
  // Ref to track if the token validity check has already run
  const hasCheckedTokenValidity = useRef(false);
  const [loadData, setLoadData] = useState<{ message?: string; text?: string }>(
    {}
  );
  const invitationCode = useSelector(
    (state: RootState) => state.auth.invitationCode
  );
  useEffect(() => {
    if (!hasCheckedTokenValidity.current && token && invitationCode == "") {
      const getPasswordTokenValid = async () => {
        const validity =
          await API_SERVICES.USER.RESET_PASSWORD_TOKEN_VALIDITY(token);
        const responseData = handleResponse(validity);
        setLoadData(responseData?.data);
        setTokenValidity(responseData?.data.message === "success");
      };

      getPasswordTokenValid();
      hasCheckedTokenValidity.current = true; // Set to true after the first call
    }
  }, [token, invitationCode]);

  const closeModal = () => {
    dispatch(modalSlice.actions.updatePasswordModal(false));
    dispatch(modalSlice.actions.updateAuthModal(false));
    navigate("/");
  };

  const validatePassword = useCallback(() => {
    return password === confirmPassword;
  }, [password, confirmPassword]);

  const setupNewPassword = useCallback(async () => {
    setIsFetching(true);
    try {
      const passwordValid = validatePassword();

      setPasswordStates(prevState => ({
        ...prevState,
        passwordMismatch: !passwordValid,
      }));

      if (!passwordValid) return;

      let data;
      if (invitationCode) {
        data = await API_SERVICES.USER.INVITED_USER_SET_PASSWORD(
          password,
          invitationCode
        );
      } else {
        data = await API_SERVICES.USER.SET_PASSWORD(password, token);
      }
      const responseData = handleResponse(data);

      if (responseData?.ok) {
        navigate(routeEnum.Home);
        dispatch(modalSlice.actions.updatePasswordModal(false));
        dispatch(modalSlice.actions.updateAuthModal(false));
        dispatch(modalSlice.actions.updateLoginModal(true));

        dispatch(
          localSlice.actions.userAuthenticated({
            isAuthenticated: false,
            refresh_token: "",
            token: "",
          })
        );

        commonUtils.toastService(
          t("passwordModal.passwordResetSuccessfully"),
          CONSTANTS.TOAST_TYPE.SUCCESS
        );
      }
    } catch (error) {
      console.error("Error while setting up new password:", error);
    } finally {
      // Always stop loader
      setIsFetching(false);
    }
  }, [password, confirmPassword]);

  return (
    <Modals
      flag={true}
      footerClassName="fg-modal-footer"
      onClose={closeModal}
      modalClassName="password passwordModal"
      contentClassName="fg-modal-body-bg"
      isModalHeader={false}
      footerContent={<FooterModal />}
    >
      <>
        {isFetching && <Loader isLoading={true} />}
        <div
          className="container-fluid fg-z-index-1 position-relative"
          data-testid="login-header"
        >
          <div className="fg-logo-within-shape">
            <img
              src={AVAILIO_LOGO}
              className="fg-logo-img"
              alt={t("availioTitle")}
            />
          </div>
        </div>

        <div className="container-fluid fg-z-index-1 position-relative modal-form-content mt-5 mt-lg-0">
          <div className="row">
            <div className="col-sm-12 col-md-6 col-xl-4 offset-md-3 offset-xl-4">
              {invitationCode == "" && !tokenValidity ? (
                <div>
                  <h4 className="fg-password-invalid-token-wrapper fg-text-error mb-2 text-center">
                    {loadData?.message != "success" && (
                      <>
                        <i className="fa-sharp fa-solid fa-xmark fa-xl"></i>
                        {loadData?.message}
                      </>
                    )}
                  </h4>
                  {loadData?.message != "success" && (
                    <div className="d-flex flex-column align-items-center">
                      <div className="text-center mb-4">{loadData?.text}</div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <h1>{t("setupNewPassword")}</h1>
                  <div className={"form-floating mb-3 fg-input-with-icon"}>
                    <PasswordLabel
                      isIcon={true}
                      labelText={t("createNewPasswordLabel")}
                      onChange={e => setPassword(e.target.value)}
                      testId="password"
                      showStrength={true}
                      onStrengthChange={setPasswordStrength}
                      isError={false}
                    />
                  </div>
                  <div className={"form-floating mb-3 fg-input-with-icon"}>
                    <PasswordLabel
                      isIcon={true}
                      labelText={t("confirmNewPasswordLabel")}
                      onChange={e => setConfirmPassword(e.target.value)}
                      testId="confirm-password"
                      isError={passwordStates.passwordMismatch}
                      showStrength={false}
                    />
                    {passwordStates.passwordMismatch && (
                      <div className="ps-3 form-text-error">
                        {t("passwordMismatch")}
                      </div>
                    )}
                  </div>
                  <CustomButton
                    onClick={setupNewPassword}
                    disabled={passwordStrength < 7 || isFetching}
                    buttonText={t("passwordModal.setPassword")}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </>
    </Modals>
  );
};

const PasswordModal = withTranslation()(PasswordModalComponent);

export default PasswordModal;
